/**
 * PWA install service — the Android/Chromium half of "add HomePlated to your
 * Home Screen." Captures Chrome's `beforeinstallprompt` event so we can fire
 * the native one-tap install dialog on our own terms (from the Settings
 * "Install app" card) instead of relying on Chrome's passive mini-infobar,
 * and tracks install via the `appinstalled` event.
 *
 * iOS has no equivalent event — installs there are manual (Share → Add to
 * Home Screen), surfaced via InstallInstructionsModal. This service only
 * arms the native path; when no deferred prompt is available (event hasn't
 * fired yet, or a non-Chromium browser like Firefox/Samsung Internet), the
 * caller falls back to the same manual instructions modal.
 *
 * Pub/sub singleton, same shape as pushPermissionModalService: a module-level
 * deferred-event stash + listeners so UI can reactively reflect whether a
 * native prompt is currently available.
 */

// `beforeinstallprompt` isn't in the TS DOM lib — minimal local shape.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Listener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
let initialized = false;
const listeners: Set<Listener> = new Set();

function notify() {
  listeners.forEach((l) => l());
}

/**
 * Attach the window listeners. Call once, as early as possible (main.tsx,
 * before React renders) — `beforeinstallprompt` can fire before the app
 * mounts, and a missed event means no native prompt for the whole session.
 * Idempotent + SSR-safe.
 */
export function initPwaInstall(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppress Chrome's passive infobar so the Settings card owns the timing,
    // and stash the event for promptInstall() to fire later.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    // Only reliable "the user installed" signal Android gives us. Clear the
    // now-consumed prompt; hook point for analytics later.
    installed = true;
    deferredPrompt = null;
    notify();
  });
}

/** Is a native install dialog available to fire right now? */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Did the app get installed during this session (appinstalled fired)? */
export function wasInstalledThisSession(): boolean {
  return installed;
}

/**
 * Fire the native install dialog. Resolves to the user's choice, or
 * 'unavailable' if no deferred prompt is stashed. The event is single-use —
 * we clear it after one call (Chrome may re-fire `beforeinstallprompt` later,
 * which re-arms this).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const evt = deferredPrompt;
  if (!evt) return 'unavailable';
  deferredPrompt = null;
  notify();
  await evt.prompt();
  const { outcome } = await evt.userChoice;
  return outcome;
}

/**
 * Subscribe to availability changes (prompt armed/consumed, app installed).
 * Returns an unsubscribe fn. Does NOT fire immediately — read
 * canPromptInstall() for the current value.
 */
export function subscribeToPwaInstall(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
